import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY,
  DIVIDEND_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/dividends.tsx';
import {
  GenerateReportRequest,
  GenerateReportResponse,
  GetReportListResponse,
  GetReportsListV1SrReportsGetParams,
} from 'api';

export const REPORT_LIST_QUERY_KEY = 'vestra/report-list';

export const useGetReportInfiniteList = ({
  payload,
  disabled = false,
}: {
  payload: GetReportsListV1SrReportsGetParams;
  disabled?: boolean;
}) => {
  const { page, ...data } = payload;

  return useInfiniteQuery<GetReportListResponse>({
    queryKey: [REPORT_LIST_QUERY_KEY, JSON.stringify(data)],
    staleTime: 999 * 60 * 60, // 1 hours
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.v1.getReportsListV1SrReportsGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    enabled: !disabled,
    refetchInterval: 1000 * 60 * 5, //5min
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useGetReportList = ({
  payload,
  disabled = false,
  onCompleted,
}: {
  payload: GetReportsListV1SrReportsGetParams;
  disabled?: boolean;
  onCompleted?: (data?: GetReportListResponse) => number | false;
}) => {
  return useQuery<GetReportListResponse>({
    queryKey: [REPORT_LIST_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 999 * 60 * 60, // 1 hours
    queryFn: async () => {
      const response = await api.v1.getReportsListV1SrReportsGet(payload);
      return response.data;
    },
    enabled: !disabled,
    refetchInterval: onCompleted
      ? data => {
          return onCompleted(data);
        }
      : 999 * 60 * 5,
  });
};

export const useCreateReportMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: (data: GenerateReportResponse) => Promise<void> | void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: GenerateReportRequest) => {
      const response = await withErrorHandling(api.v1.createReportV1SrReportsPost(payload), {
        loading: 'Generating report...',
        success: 'Report generated successfully',
        setErrors,
      });
      return response.data;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries({ queryKey: [DIVIDEND_BY_ID_QUERY_KEY] });
      await queryClient.invalidateQueries([CURRENT_ISSUER_DIVIDEND_EVENT_LIST_QUERY_KEY]);
      onSuccess && (await onSuccess(data));
    },
  });
};

export const useDeleteReportMutation = () => {
  return useMutation({
    mutationFn: async (reportId: number) => {
      const response = await withErrorHandling(
        api.v1.deleteReportV1SrReportsIdDelete({ id: reportId }),
        {
          loading: 'Deleting report...',
          success: 'Report deleted successfully',
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);
    },
  });
};
